import React, {useEffect, useState} from "react";
import { Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import DragDropIcon from "../../images/drag_drop.svg";
import { updateCorrectAnswers} from "../../utils/questionUpdate";
import {stripHTMLTags} from "../../utils/helpers";
import {
    ASCII_CODE_A, GRADE_SCHEMA,
    MC_ANSWER_LENGTH,
} from "../../utils/constants";
import "../styles.css";

import {EditorContent, useEditor} from "@tiptap/react";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import {ReactComponent as TOGGLE} from "../../images/font_gray.svg";
import {ReactComponent as DRAG} from "../../images/drag_flat_grey.svg";
import {ReactComponent as DELETE} from "../../images/trash_red.svg";
import { ReactComponent as CORRECT_ICON } from "../../images/grade-question-correct-icon.svg";
import {Color} from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import classes from "../questions.module.css";
import {TipTapMenuBarNew} from "../../components/Tiptap/TipTapMenuBarNew";
import TiptapClasses from "../../components/Tiptap/Tiptap.module.css";

export default function CIQuestionOptionNew({
                                                gradeSchema,
                                                optionData,
                                                selectedQuestion,
                                                questions,
                                                setQuestions,
                                                index,
                                                questionBuilder = false,
                                                disabled = false,
                                                collapsed,
                                                setCollapsed,
                                                deleteCollapsed,
}) {
    const { t } = useTranslation();

    const { serialNo, correctAnswers=[], weightage } = selectedQuestion;
    const gradeEnable = gradeSchema !== GRADE_SCHEMA.UNGRADED.key && !!weightage;
    const selectedQuestionIndex = serialNo - 1;

    const editor = useEditor({
        shouldRerenderOnTransaction: true,
        editorProps: {
            transformPastedHTML(text) {
                return stripHTMLTags(text);
            },
        },
        extensions: [
            Underline,
            StarterKit,
            Placeholder.configure({
                placeholder: t('accessibility.optionLabel') + " " + String.fromCharCode(index + ASCII_CODE_A),
            }),
            CharacterCount.configure({limit: MC_ANSWER_LENGTH}),
            TextStyle,
            Color,
            Highlight,
            // Mathematics.configure({
            //     regex: LATEX_REG_EXP,
            // }),
        ],
        onUpdate: ({editor}) => {
            handleAnswerChange(editor.getHTML(), index);
        },
        content: optionData.text ? optionData.text : "",
    }, [serialNo]);

    useEffect(() => {
        let text = optionData.text ? optionData.text : "";
        if (text !== editor.getHTML()) {
            editor.commands.setContent(text);
        }

    }, [optionData, editor]);


    const handleAnswerChange = (input, itemKey) => {
        const copyOfQuestions = [...questions];
        const newOptionsMap = [...copyOfQuestions[selectedQuestionIndex].optionsMap];
        newOptionsMap[itemKey].text = input;
        copyOfQuestions[selectedQuestionIndex].optionsMap = newOptionsMap;
        setQuestions(copyOfQuestions);
    };

    const handleCorrectAnswerChange = (input) => {
        setQuestions(updateCorrectAnswers(input, selectedQuestionIndex, questions));
    };

    const handleDeleteOption = () => {
        let newQuestions = [];

        questions.forEach((q) => {
            if (q.serialNo === serialNo) {
                const newOptionsMap = [...q.optionsMap];
                newOptionsMap.splice(index, 1);
                let updatedQuestion = {
                    ...q,
                    optionsMap: newOptionsMap,
                    correctAnswers:
                        q.correctAnswers.includes(index) ?  q.correctAnswers.filter(item => item !== index)  : q.correctAnswers,
                };
                newQuestions.push(updatedQuestion);
            } else {
                newQuestions.push({ ...q });
            }
        });
        deleteCollapsed();
        setQuestions(newQuestions);
    };


    const renderIndexAndImage = (dragHandleProps) => {
        return (
            <>
                <span {...dragHandleProps}  style={{marginLeft: 12}}>
                    <DRAG/>
                </span>
                <span className={`${classes.optionIndex} ${gradeEnable && correctAnswers.indexOf(index) !== -1 ? classes.correct : ""}`}>
                    {String.fromCharCode(index + ASCII_CODE_A)}
                </span>
            </>
        );
    }


    const renderOptionManageBar = () => {
        return (
            <div style={{display: "flex", gap: 5, alignItems: "center", paddingRight: 12}}>
                <span
                    tabIndex={0}
                    style={{cursor: "pointer",}}
                    title={t('poll.toggleMenuBarText')}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <TOGGLE/>
                </span>
                <span className={classes.iconDivider}/>
                <span
                    tabIndex={0}
                    style={{cursor: "pointer",}}
                    title={t('accessibility.deleteOptionButtonPrefix')}
                    onClick={handleDeleteOption}
                >
                   <DELETE/>
                </span>
            </div>
        );
    }


    return (
        <Draggable
            draggableId={`${index}`}
            index={index}
            key={index}
            isDragDisabled={questionBuilder && disabled}
        >
            {(provided) => (
                <div
                    tabIndex={-1}
                    className={classes.optionBarWrapper}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                >

                    {gradeEnable &&
                        <div className={classes.optionCheckBoxContainer}>
                            <span
                                tabIndex={0}
                                onClick={() => handleCorrectAnswerChange(index)}
                                className={`${classes.optionCheckBox} ${correctAnswers.indexOf(index) !== -1 ? classes.active : ""}`}
                                onKeyDown={(event) => {
                                    if (event.key === ' ') {
                                        event.preventDefault();
                                        handleCorrectAnswerChange(index);
                                    }
                                }}
                            >
                                {correctAnswers.indexOf(index) !== -1 && <CORRECT_ICON/>}
                            </span>
                        </div>
                    }


                    <div className={classes.optionBar}>
                        {!!collapsed && renderIndexAndImage(provided.dragHandleProps)}

                        <span className={classes.optionEditorWrapper}>
                            <div className={`${classes.optionEditorMenuBar} ${collapsed ? classes.collapsed : ""}`}>
                                <TipTapMenuBarNew
                                    isOpen={!collapsed}
                                    editor={editor}
                                    selectedQuestion={selectedQuestion}
                                />
                                {!collapsed && renderOptionManageBar()}
                            </div>

                            <div style={{display: "flex", gap: 10, alignItems: "center"}}>
                                {!collapsed && renderIndexAndImage(provided.dragHandleProps)}
                                <EditorContent
                                    editor={editor}
                                    style={{cursor: "text", flex: 1, minWidth: 0, height: "100%", paddingRight: collapsed ? 10 : undefined}}
                                    className={`${TiptapClasses.editorContentBox} ${collapsed ? TiptapClasses.noneOutLine : ""}`}
                                />
                            </div>


                            {(!collapsed || (!!editor.isFocused && editor.storage.characterCount.characters() > 0)) &&
                                <span className={`${classes.characterCountBox} ${collapsed ? classes.inline : ""}`}>
                                    {collapsed
                                        ? editor.storage.characterCount.characters()
                                        : `${editor.storage.characterCount.characters()}/${MC_ANSWER_LENGTH}`
                                    }
                                </span>
                            }


                        </span>

                        {!!collapsed && renderOptionManageBar()}
                    </div>
                </div>
            )}
        </Draggable>
    );
}
